import { useState, useRef } from 'react';
import api from '../api/client';

export const useWalmartStockUpload = (loadProducts) => {
  const [showWalmartStockPopup, setShowWalmartStockPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Handle file selection and preview (test upload)
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setParsedData([]);
    setUploadStatus(null);

    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().match(/\.[0-9a-z]+$/)?.[0];
    if (!validTypes.includes(fileExt)) {
      setUploadStatus({
        type: 'error',
        message: 'Invalid file type. Please upload CSV or Excel file.'
      });
      return;
    }

    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadStatus({
        type: 'error',
        message: 'File is too large. Maximum size is 10MB.'
      });
      return;
    }

    setUploadStatus({ type: 'loading', message: 'Parsing file...' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post('/api/upload-walmart-stock-test', formData);

      setParsedData(data.preview || []);
      const unmatchedInfo = data.unmatchedCount
        ? ` · ${data.unmatchedCount} not matched: ${(data.unmatchedList || []).slice(0, 8).join(', ')}${data.unmatchedCount > 8 ? '…' : ''}`
        : '';
      setUploadStatus({
        type: 'success',
        message: `File parsed: ${data.totalRows || 0} rows → ${data.validSKUs || 0} SKUs matched`,
        details: unmatchedInfo || 'All rows matched'
      });
    } catch (error) {
      console.error('Error parsing Walmart stock file:', error);
      const message = error.response ? (error.response.data?.error || 'Failed to parse file') : error.message;
      setUploadStatus({
        type: 'error',
        message: `Error parsing file: ${message}`,
        details: 'Please check the file format and try again.'
      });
    }
  };

  // Submit actual stock data to database
  const handleUploadSubmit = async () => {
    if (!selectedFile || parsedData.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a valid file first.'
      });
      return;
    }

    setUploadStatus({ type: 'loading', message: 'Uploading stock data...' });

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const { data } = await api.post('/api/upload-walmart-stock', formData);

      setUploadStatus({
        type: 'success',
        message: `Upload successful!`,
        details: `Updated: ${data.processed} SKUs, Errors: ${data.errors}`
      });

      // Auto-close after success and refresh product data if needed
      setTimeout(() => {
        closePopup();
        if (loadProducts) loadProducts();
      }, 3000);
    } catch (error) {
      console.error('Error uploading Walmart stock:', error);
      const message = error.response ? (error.response.data?.error || 'Upload failed') : error.message;
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${message}`,
        details: 'Please try again.'
      });
    }
  };

  // Close and reset popup
  const closePopup = () => {
    setShowWalmartStockPopup(false);
    setSelectedFile(null);
    setParsedData([]);
    setUploadStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    showWalmartStockPopup,
    setShowWalmartStockPopup,
    selectedFile,
    setSelectedFile,
    parsedData,
    setParsedData,
    uploadStatus,
    setUploadStatus,
    fileInputRef,
    handleFileUpload,
    handleUploadSubmit,
    closePopup
  };
};